import * as React from "react";
import { observer } from "mobx-react";
import { withStores } from "stores";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkSecondaryButton } from "uitk-react-button";
import { UitkLayoutGrid, UitkLayoutGridItem, UitkLayoutConditionalGridColumnRow } from "uitk-react-layout-grid";
import { UitkSpacing } from "uitk-react-spacing";
import { FlexClickTracker } from "src/components/utility/analytics/FlexClickTracker/FlexClickTracker";

import { CarsFlexModuleResult } from "typings/microserviceModels/cars-flex-module";
import { CarOfferCard } from "./CarOfferCard";
import { CarsProps } from "./typings";
import { locKeys } from "./i10n";

const LOWEST_PRICE_CREATED_TIME_TOKEN_KEY = "carsLowestTotalPriceOfferCreatedTime";
const LOWEST_PRICE_UNITS_TOKEN_KEY = "carsLowestTotalPriceHoursOrDays";
const ButtonTracked = FlexClickTracker(UitkSecondaryButton);

const gridColumnsConfig: UitkLayoutConditionalGridColumnRow = { small: 1, large: 2, extra_large: 3 };

const isShowDisclaimer = (model: CarsFlexModuleResult): boolean =>
  !!(model.tokens[LOWEST_PRICE_CREATED_TIME_TOKEN_KEY] && model.tokens[LOWEST_PRICE_UNITS_TOKEN_KEY]);

const showDisclaimerText = (
  model: CarsFlexModuleResult,
  formatText: (key: string, ...args: any[]) => string
): string => {
  const lowestPriceCreatedTime: string = model.tokens[LOWEST_PRICE_CREATED_TIME_TOKEN_KEY];
  const lowestPriceCreatedTimeUnit: string = model.tokens[LOWEST_PRICE_UNITS_TOKEN_KEY];

  return formatText(locKeys.disclaimerPrefix.concat(lowestPriceCreatedTimeUnit), lowestPriceCreatedTime);
};

export const Cars = withStores(
  "flexModuleModelStore",
  "context"
)(
  observer((props: CarsProps) => {
    const {
      context: { searchContext },
      flexTemplateNode,
      flexModuleModelStore,
    } = props;

    const { formatText } = useLocalization();
    const { id } = flexTemplateNode;
    const model = flexModuleModelStore.getModel(id) as CarsFlexModuleResult | null;
    const locationName = searchContext?.location?.name;

    if (!model) {
      return null;
    }

    const { title, carList, hideOptions, showSeeAll, seeAllUrl } = model;

    return (
      <UitkSpacing margin={{ block: "three" }}>
        <div className="Cars" id={id}>
          <UitkSpacing margin={{ blockend: "three" }}>
            <UitkHeading tag="h2" size={4}>
              {title}
            </UitkHeading>
          </UitkSpacing>

          <UitkLayoutGrid space="three" columns={gridColumnsConfig}>
            {carList?.map((car) => (
              <UitkLayoutGridItem key={car.carModel}>
                <CarOfferCard carOffer={car} hideOptions={hideOptions} />
              </UitkLayoutGridItem>
            ))}
          </UitkLayoutGrid>

          {showSeeAll && (
            <div className="uitk-type-center">
              <UitkSpacing margin={{ blockstart: "six" }}>
                <UitkHeading tag="h3" size={7}>
                  {formatText(locKeys.seeAllTitle)}
                </UitkHeading>
              </UitkSpacing>

              {(searchContext.location || searchContext.carSupplierLocation) && (
                <UitkText spacing="one" size={300}>
                  {formatText(
                    locKeys.seeAllSubtitle,
                    searchContext.location
                      ? searchContext.location.name
                      : searchContext.carSupplierLocation.address.firstAddressLine
                  )}
                </UitkText>
              )}

              <UitkSpacing margin={{ blockstart: "three" }}>
                <ButtonTracked
                  moduleName={"CarDeal"}
                  rfrr={`page.TravelGuides.Cars.${locationName}.ViewAllCars`}
                  linkName="LandingPageCarDeals"
                  tag="a"
                  href={seeAllUrl}
                >
                  {formatText(locKeys.seeAllLink)}
                </ButtonTracked>
              </UitkSpacing>
            </div>
          )}
          {isShowDisclaimer(model) && (
            <UitkText spacing="three" size={100} align="center">
              {showDisclaimerText(model, formatText)}
            </UitkText>
          )}
        </div>
      </UitkSpacing>
    );
  })
);

export default Cars;
